/**
 * Groups on their way out, and the button that stops them.
 *
 * Deleting a group is four confirmations deep and still occasionally regretted
 * — an owner clears out what they think is an old trip and remembers on the
 * drive home that it had the only copy of something. For a short window it is
 * recoverable, and this is the only screen anywhere that shows a deleted group
 * at all: everywhere else it is gone, which is what deleting is for.
 *
 * The window is short because the other half of the problem is money. Until it
 * closes, every byte is still in object storage, billed, for photographs
 * nobody can open. Seventy-two hours is long enough to sleep on it and for the
 * rest of the group to object, short enough not to pay for a month of it.
 *
 * The storage summary beneath answers the question that usually arrives with
 * the bill: why is the bucket bigger than the photographs? Almost always the
 * answer is the trash, a group waiting out this window, or originals whose
 * last reference has just gone. Read-only, deliberately — the numbers explain,
 * they do not offer to delete anything.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatBytes } from "@fc/core";
import { errorMessage, trpc } from "../../lib/trpc.js";
import { Button } from "../../components/ui/Button.js";
import { Input } from "../../components/ui/Input.js";
import { Skeleton } from "../../components/ui/Skeleton.js";
import "./DeletedGroups.css";

export function DeletedGroups() {
  const queryClient = useQueryClient();
  const deleted = useQuery(trpc.admin.deletedGroups.queryOptions());
  const report = useQuery(trpc.admin.storageReport.queryOptions());

  /** Which group is being restored, and why — the reason goes in the audit. */
  const [restoring, setRestoring] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const restore = useMutation(
    trpc.admin.restoreGroup.mutationOptions({
      onSuccess: async () => {
        setRestoring(null);
        setReason("");
        setError(null);
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.admin.deletedGroups.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.admin.storageReport.queryKey(),
          }),
        ]);
      },
      onError: (caught) => setError(errorMessage(caught)),
    }),
  );

  return (
    <>
      <section className="fc-admin__section" aria-labelledby="fc-admin-deleted">
        <h2 className="fc-admin__section-title" id="fc-admin-deleted">
          Deleted groups
        </h2>

        {deleted.isLoading ? (
          <Skeleton height="72px" radius="md" />
        ) : deleted.isError || !deleted.data ? (
          <p className="fc-admin__error" role="alert">
            {errorMessage(deleted.error)}
          </p>
        ) : deleted.data.groups.length === 0 ? (
          <p className="fc-admin__empty">
            Nothing waiting to be deleted. A group deleted in the last{" "}
            {deleted.data.windowHours} hours appears here, with its photographs
            still recoverable.
          </p>
        ) : (
          <ul className="fc-deleted" role="list">
            {deleted.data.groups.map((group) => {
              const deletedAt = new Date(group.deletedAt);
              const hoursLeft = Math.max(
                0,
                deleted.data.windowHours -
                  (Date.now() - deletedAt.getTime()) / 3_600_000,
              );

              return (
                <li key={group.id} className="fc-deleted__row">
                  <div className="fc-deleted__what">
                    <strong>{group.name}</strong>
                    <span className="fc-deleted__meta">
                      {group.fileCount.toLocaleString()} files ·{" "}
                      {formatBytes(group.usedBytes)} · deleted{" "}
                      {deletedAt.toLocaleString()}
                    </span>
                  </div>

                  {/*
                    A countdown rather than a deadline, because "in 4 hours" is
                    a decision and "at 03:12" is arithmetic somebody has to do
                    while deciding.
                  */}
                  <span
                    className="fc-deleted__left"
                    data-urgent={hoursLeft < 6 || undefined}
                  >
                    {hoursLeft <= 0
                      ? "Purging now"
                      : hoursLeft < 1
                        ? `${String(Math.ceil(hoursLeft * 60))} min left`
                        : `${String(Math.floor(hoursLeft))} h left`}
                  </span>

                  {restoring === group.id ? (
                    <form
                      className="fc-deleted__form"
                      onSubmit={(event) => {
                        event.preventDefault();
                        restore.mutate({ groupId: group.id, reason });
                      }}
                    >
                      <Input
                        value={reason}
                        placeholder="Why — goes in the audit log"
                        onChange={(event) => setReason(event.target.value)}
                        autoFocus
                      />
                      <Button
                        type="submit"
                        variant="primary"
                        loading={restore.isPending}
                        disabled={reason.trim().length < 3}
                      >
                        Restore
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => {
                          setRestoring(null);
                          setError(null);
                        }}
                      >
                        Cancel
                      </Button>
                    </form>
                  ) : (
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setRestoring(group.id);
                        setReason("");
                        setError(null);
                      }}
                    >
                      Restore
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {error && (
          <p className="fc-admin__error" role="alert">
            {error}
          </p>
        )}
      </section>

      <section className="fc-admin__section" aria-labelledby="fc-admin-bytes">
        <h2 className="fc-admin__section-title" id="fc-admin-bytes">
          Where the stored bytes are
        </h2>

        {report.isLoading ? (
          <Skeleton height="88px" radius="md" />
        ) : report.isError || !report.data ? (
          <p className="fc-admin__error" role="alert">
            {errorMessage(report.error)}
          </p>
        ) : (
          <>
            <dl className="fc-admin__stats">
              <Figure label="Live" value={report.data.liveBytes} />
              <Figure label="In the trash" value={report.data.trashedBytes} />
              <Figure
                label="Groups being deleted"
                value={report.data.deletingGroupBytes}
              />
              <Figure
                label="Awaiting the sweep"
                value={report.data.orphanBlobBytes}
                hint={`${report.data.orphanBlobCount.toLocaleString()} objects nothing references`}
              />
            </dl>

            <p className="fc-admin__empty">
              {formatBytes(report.data.accountedBytes)} accounted for. Object
              storage larger than this is usually incomplete uploads, which the
              bucket’s own lifecycle rule expires — nothing here deletes
              anything, it only explains where the bytes went.
            </p>
          </>
        )}
      </section>
    </>
  );
}

/** The same shape as the overview counters, so the two pages read as one. */
function Figure({
  label,
  value,
  hint,
}: {
  label: string;
  value: number;
  hint?: string;
}) {
  return (
    <div className="fc-admin__counter">
      <dt className="fc-admin__counter-label">{label}</dt>
      <dd className="fc-admin__counter-value fc-tabular">
        {formatBytes(value)}
      </dd>
      {hint && <dd className="fc-admin__counter-hint fc-tabular">{hint}</dd>}
    </div>
  );
}
